import React, { useState, useRef, useEffect } from 'react';
import { FiUpload, FiX, FiImage, FiCheck, FiLoader, FiCrop, FiRefreshCw } from 'react-icons/fi';
import { blockApi } from '../../../../../services/api';
import './ImageUpload.css';

// Image processing configuration
const IMAGE_CONFIG = {
  maxSizeKB: 100, // 100KB max file size
  targetWidth: 400, // Standard width in pixels
  targetHeight: 400, // Standard height in pixels (square)
  quality: 0.85, // Initial compression quality
  format: 'image/jpeg' // Output format
};

const ImageUpload = ({ 
  blockSerial, 
  imageType = 'profile', 
  currentImageUrl, 
  onImageUploaded, 
  onImageDeleted 
}) => {
  const [preview, setPreview] = useState(currentImageUrl || null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef(null);

  // Load existing image when component mounts or currentImageUrl changes
  useEffect(() => {
    setPreview(currentImageUrl || null);
  }, [currentImageUrl]);

  /**
   * Process image: crop to square, resize, and compress
   */
  const processImage = (file) => {
    return new Promise((resolve, reject) => {
      setProcessing(true);
      
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      img.onload = () => {
        try {
          // Calculate square crop dimensions (center on largest dimension)
          const size = Math.min(img.width, img.height);
          const offsetX = (img.width - size) / 2;
          const offsetY = (img.height - size) / 2;
          
          // Set canvas to target size
          canvas.width = IMAGE_CONFIG.targetWidth;
          canvas.height = IMAGE_CONFIG.targetHeight;
          
          // Draw cropped and resized image
          ctx.drawImage(
            img,
            offsetX, offsetY, size, size, // Source crop area
            0, 0, IMAGE_CONFIG.targetWidth, IMAGE_CONFIG.targetHeight // Destination
          );
          
          // Compress image iteratively until under size limit
          compressImage(canvas, IMAGE_CONFIG.quality)
            .then(resolve)
            .catch(reject)
            .finally(() => setProcessing(false));
            
        } catch (error) {
          setProcessing(false);
          reject(error);
        }
      };
      
      img.onerror = () => {
        setProcessing(false);
        reject(new Error('Failed to load image'));
      };
      
      // Create object URL for the image
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };
  
  /**
   * Compress image to target size
   */
  const compressImage = (canvas, quality) => {
    return new Promise((resolve, reject) => {
      const compress = (currentQuality) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }
            
            const sizeKB = blob.size / 1024;
            
            if (sizeKB <= IMAGE_CONFIG.maxSizeKB || currentQuality <= 0.3) {
              // Convert to base64 for upload
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = () => reject(new Error('Failed to read compressed image'));
              reader.readAsDataURL(blob);
            } else {
              // Reduce quality and try again
              compress(currentQuality - 0.1);
            }
          },
          IMAGE_CONFIG.format,
          currentQuality
        );
      };
      
      compress(quality);
    });
  };

  const handleFileSelect = async (file) => {
    console.log('🎯 handleFileSelect called:', {
      file: file?.name,
      type: file?.type,
      size: file?.size,
      blockSerial
    });
    
    if (!file) return;
    
    try {
      console.log('🔄 Processing image...', file.name);
      const processedImage = await processImage(file);
      console.log('✅ Image processed successfully, length:', processedImage.length);
      setPreview(processedImage);
      
      // Auto-upload the processed image
      console.log('🚀 Auto-uploading processed image...');
      await uploadProcessedImage(processedImage);
    } catch (error) {
      console.error('❌ Image processing/upload error:', error);
      alert('Failed to process or upload image. Please try again.');
    }
  };

  const uploadProcessedImage = async (imageData) => {
    if (!imageData || !blockSerial) {
      console.error('Missing image data or block serial');
      return;
    }

    setUploading(true);
    console.log('🔄 Starting upload process...', {
      blockSerial,
      imageDataLength: imageData?.length,
      imageType,
      timestamp: new Date().toISOString()
    });

    try {
      console.log('📤 Calling API updateBlockImage...');
      
      // Add timeout to prevent infinite loading
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Upload timeout after 30 seconds')), 30000)
      );
      
      const uploadPromise = blockApi.updateBlockImage(blockSerial, imageData, imageType);
      const result = await Promise.race([uploadPromise, timeoutPromise]);
      
      console.log('📥 Upload result:', result);
      
      if (result && result.success) {
        console.log('✅ Upload successful! Image URL:', result.imageUrl);
        
        // Update the preview with the server URL if provided
        if (result.imageUrl) {
          setPreview(result.imageUrl);
        }
        
        // Notify parent component
        if (onImageUploaded) {
          onImageUploaded(result.imageUrl);
        }
        
        // Show success message temporarily
        setUploadSuccess(true);
        setTimeout(() => {
          setUploadSuccess(false);
        }, 3000); // Hide after 3 seconds
      } else {
        throw new Error(result?.message || 'Upload failed');
      }
    } catch (error) {
      console.error('❌ Upload error:', error);
      
      // Provide user-friendly error messages
      let errorMessage = error.message || 'Unknown error occurred';
      if (error.message.includes('timeout')) {
        errorMessage = 'Upload timed out. Please check your connection and try again.';
      } else if (error.message.includes('network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.message.includes('auth')) {
        errorMessage = 'Authentication failed. Please refresh the page and try again.';
      } else if (error.message.includes('size')) {
        errorMessage = 'Image too large. Please use a smaller image.';
      }
      
      alert(`Upload failed: ${errorMessage}`);
      // Reset preview on error
      setPreview(currentImageUrl || null);
    } finally {
      setUploading(false);
    }
  };

  const uploadImage = async () => {
    if (!preview) {
      alert('No image to upload');
      return;
    }
    
    await uploadProcessedImage(preview);
  };

  const deleteImage = async () => {
    try {
      setDeleting(true);
      const result = await blockApi.deleteBlockImage(blockSerial, imageType);
      
      if (result.success) {
        setPreview(null);
        onImageDeleted(imageType);
      } else {
        throw new Error(result.message || 'Delete failed');
      }
    } catch (error) {
      console.error('Image delete error:', error);
      alert('Failed to delete image. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  return (
    <div className="image-upload-container">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      <div 
        className={`image-upload-zone ${dragActive ? 'drag-active' : ''} ${preview ? 'has-image' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={!preview ? handleClick : undefined}
      >
        {preview ? (
          <div className="image-preview">
            <img src={preview} alt="Block preview" className="preview-image" />
            <div className="image-overlay">
              <div className="image-actions">
                <button
                  className="action-button upload-button"
                  onClick={uploadImage}
                  disabled={uploading || deleting}
                  title="Upload image"
                >
                  {uploading ? <FiLoader className="spinning" /> : <FiUpload />}
                </button>
                <button
                  className="action-button change-button"
                  onClick={handleClick}
                  disabled={uploading || deleting}
                  title="Change image"
                >
                  <FiRefreshCw />
                </button>
                <button
                  className="action-button delete-button"
                  onClick={deleteImage}
                  disabled={uploading || deleting}
                  title="Delete image"
                >
                  {deleting ? <FiLoader className="spinning" /> : <FiX />}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="upload-prompt">
            <FiImage className="upload-icon" />
            <p className="upload-text">
              {uploading ? 'Uploading...' : 'Drop image here or click to upload'}
            </p>
            <p className="upload-hint">JPEG, PNG, GIF, WebP • Max 5MB</p>
            {uploading && <FiLoader className="upload-spinner spinning" />}
          </div>
        )}
      </div>

      {uploadSuccess && (
        <div className="upload-status">
          <FiCheck className="success-icon" />
          <span>Image uploaded successfully</span>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
